import React, { useState, useMemo } from 'react'
import { SprintUserStory } from '@jirax/common'
import clsx from 'clsx'
import {
  ArrowRightIcon,
  PlusCircleIcon,
  MinusCircleIcon,
  ChatBubbleLeftRightIcon,
  CodeBracketIcon,
  ClockIcon
} from '@heroicons/react/20/solid'
import type { UserAnalysis, TimelineEvent, TimelineEventType } from '../types'
import { ActivityChart } from './ActivityChart'

const keyPill = (issueKey: string) => (
  <a
    href={`https://cnsgitbits.atlassian.net/browse/${issueKey}`}
    target='_blank'
    rel='noopener noreferrer'
    className='ml-1.5 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600 hover:bg-indigo-100 hover:text-indigo-700 transition-colors'
    onClick={e => e.stopPropagation()}
  >
    {issueKey}
  </a>
)

const formatTime = (timestamp: string) => {
  const date = new Date(timestamp)
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

export const TimelineView: React.FC<{
  userStory: SprintUserStory
  issueTitleMap: Map<string, string>
  changes?: UserAnalysis['changes']
}> = ({ userStory, issueTitleMap, changes }) => {
  const [showAllStuck, setShowAllStuck] = useState(false)
  const [showAllComments, setShowAllComments] = useState(false)

  const timelineEvents = useMemo<TimelineEvent[]>(() => {
    const events: TimelineEvent[] = []
    if (!userStory?.ticketMovements) return events

    userStory.ticketMovements.forEach(movement => {
      events.push({
        type: 'movement',
        timestamp: movement.changedAt,
        issueKey: movement.issueKey,
        fromStatus: movement.fromStatus,
        toStatus: movement.toStatus
      })
    })

    userStory.scopeChanges.forEach(change => {
      events.push({
        type: change.type === 'added' ? 'scope-add' : 'scope-remove',
        timestamp: change.changedAt,
        issueKey: change.issueKey,
        points: change.points
      })
    })

    userStory.commentActivity.forEach(activity => {
      (activity.commentDates ?? []).forEach(date => {
        events.push({
          type: 'comment',
          timestamp: date,
          issueKey: activity.issueKey,
          commentCount: 1
        })
      })
    })

    userStory.prLinks.forEach(pr => {
      const issueKeyMatch = pr.title?.match(/^([A-Z][A-Z0-9]*-\d+)/i)
      const issueKey = issueKeyMatch ? issueKeyMatch[1].toUpperCase() : ''
      events.push({
        type: 'pr',
        timestamp: pr.createdAt || new Date().toISOString(),
        issueKey,
        prUrl: pr.url,
        prTitle: pr.title,
        prState: pr.state,
        prLinesAdded: pr.linesAdded,
        prLinesRemoved: pr.linesRemoved
      })
    })

    return events.sort((a, b) =>
      new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
    )
  }, [userStory])

  const getEventIcon = (type: TimelineEventType) => {
    switch (type) {
      case 'movement': return <ArrowRightIcon className='h-4 w-4 text-blue-600' />
      case 'scope-add': return <PlusCircleIcon className='h-4 w-4 text-green-600' />
      case 'scope-remove': return <MinusCircleIcon className='h-4 w-4 text-red-600' />
      case 'comment': return <ChatBubbleLeftRightIcon className='h-4 w-4 text-purple-600' />
      case 'pr': return <CodeBracketIcon className='h-4 w-4 text-orange-600' />
    }
  }

  const getEventLabel = (event: TimelineEvent) => {
    const title = issueTitleMap.get(event.issueKey)
    switch (event.type) {
      case 'movement':
        return (
          <span className='flex items-center flex-wrap gap-x-1'>
            {title && <span className='text-gray-800'>{title}</span>}
            {keyPill(event.issueKey)}
            <span className='text-gray-500'>moved</span>
            <span className='font-medium text-gray-700'>{event.fromStatus}</span>
            <span className='text-gray-400'>→</span>
            <span className='font-medium text-gray-700'>{event.toStatus}</span>
          </span>
        )
      case 'scope-add':
        return (
          <span className='flex items-center flex-wrap gap-x-1'>
            {title && <span className='text-gray-800'>{title}</span>}
            {keyPill(event.issueKey)}
            <span className='text-green-600'>added</span>
            <span className='font-medium text-green-700'>{event.points} pts</span>
          </span>
        )
      case 'scope-remove':
        return (
          <span className='flex items-center flex-wrap gap-x-1'>
            {title && <span className='text-gray-800'>{title}</span>}
            {keyPill(event.issueKey)}
            <span className='text-red-600'>removed</span>
            <span className='font-medium text-red-700'>{event.points} pts</span>
          </span>
        )
      case 'comment':
        return (
          <span className='flex items-center flex-wrap gap-x-1'>
            {title && <span className='text-gray-800'>{title}</span>}
            {keyPill(event.issueKey)}
            <span className='text-gray-500'>added</span>
            <span className='font-medium text-purple-700'>{event.commentCount} comment{event.commentCount !== 1 ? 's' : ''}</span>
          </span>
        )
      case 'pr':
        return (
          <span className='flex items-center flex-wrap gap-x-1'>
            <a
              href={event.prUrl}
              target='_blank'
              rel='noopener noreferrer'
              className='text-gray-800 hover:text-indigo-600 hover:underline'
              onClick={e => e.stopPropagation()}
            >
              {event.prTitle || 'PR'}
            </a>
            {event.issueKey && keyPill(event.issueKey)}
            <span className={clsx(
              'rounded px-2 py-0.5 text-xs font-medium',
              event.prState === 'merged' && 'bg-green-100 text-green-800',
              event.prState === 'open' && 'bg-yellow-100 text-yellow-800',
              event.prState === 'closed' && 'bg-gray-100 text-gray-800'
            )}>
              {event.prState}
            </span>
            {(event.prLinesAdded != null || event.prLinesRemoved != null) && (
              <span className='text-xs text-gray-500'>
                {event.prLinesAdded != null && <span className='text-green-600'>+{event.prLinesAdded.toLocaleString()}</span>}
                {event.prLinesAdded != null && event.prLinesRemoved != null && <span className='text-gray-400'> / </span>}
                {event.prLinesRemoved != null && <span className='text-red-500'>-{event.prLinesRemoved.toLocaleString()}</span>}
              </span>
            )}
          </span>
        )
    }
  }

  // Sprint summary: flow, stuck, comments
  const transitionMap = new Map<string, number>()
  userStory.ticketMovements.forEach(m => {
    const key = `${m.fromStatus}|||${m.toStatus}`
    transitionMap.set(key, (transitionMap.get(key) ?? 0) + 1)
  })

  const WORKFLOW_ORDER = [
    'Backlog', 'In Progress', 'Code Review', 'Dev Review',
    'Ready to Stage', 'Stage Review', 'Ready for Release', 'Done'
  ]
  const workflowRank = (s: string) => {
    const i = WORKFLOW_ORDER.indexOf(s)
    return i >= 0 ? i : 999
  }

  const graph = new Map<string, { to: string; count: number }[]>()
  const inDegree = new Map<string, number>()
  transitionMap.forEach((count, key) => {
    const [from, to] = key.split('|||')
    if (workflowRank(to) <= workflowRank(from)) return
    if (!graph.has(from)) graph.set(from, [])
    graph.get(from)!.push({ to, count })
    inDegree.set(to, (inDegree.get(to) ?? 0) + 1)
    if (!inDegree.has(from)) inDegree.set(from, 0)
  })

  const allNodes = Array.from(new Set([...graph.keys(), ...[...transitionMap.keys()].map(k => k.split('|||')[1])]))

  function longestPath(start: string): { node: string; countToNext?: number }[] {
    let best: { node: string; countToNext?: number }[] = []
    function dfs(node: string, path: { node: string; countToNext?: number }[], visited: Set<string>) {
      if (path.length > best.length) best = [...path]
      const edges = (graph.get(node) ?? []).sort((a, b) => b.count - a.count)
      for (const edge of edges) {
        if (!visited.has(edge.to)) {
          path[path.length - 1] = { node, countToNext: edge.count }
          visited.add(edge.to)
          dfs(edge.to, [...path, { node: edge.to }], visited)
          visited.delete(edge.to)
        }
      }
    }
    const visited = new Set([start])
    dfs(start, [{ node: start }], visited)
    return best
  }

  const startCandidates = allNodes.filter(n => (inDegree.get(n) ?? 0) === 0 && graph.has(n))
  let chain: { node: string; countToNext?: number }[] = []
  for (const candidate of startCandidates) {
    const path = longestPath(candidate)
    if (path.length > chain.length) chain = path
  }
  if (chain.length === 0 && graph.size > 0) {
    const fallbackStart = Array.from(graph.keys())
      .sort((a, b) => workflowRank(a) - workflowRank(b))[0]
    chain = longestPath(fallbackStart)
  }

  const totalComments = userStory.commentActivity.reduce((s, a) => s + a.commentCount, 0)
  const movedKeys = new Set(userStory.ticketMovements.map(m => m.issueKey))
  const stuckKeys = (userStory.startingIssueKeys ?? []).filter(k => !movedKeys.has(k))

  const rejectionsBySource = new Map<string, { to: string; count: number }[]>()
  transitionMap.forEach((count, key) => {
    const [from, to] = key.split('|||')
    if (workflowRank(to) < workflowRank(from)) {
      if (!rejectionsBySource.has(from)) rejectionsBySource.set(from, [])
      rejectionsBySource.get(from)!.push({ to, count })
    }
  })

  return (
    <div className='space-y-4'>
      <div className='grid grid-cols-2 gap-4'>
        <div className='group relative rounded-lg bg-blue-50 p-4 flex items-center gap-4'>
          <div className={clsx(
            'flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full text-xl font-bold',
            userStory.engagement.score >= 80 ? 'bg-green-100 text-green-700' :
              userStory.engagement.score >= 50 ? 'bg-yellow-100 text-yellow-700' :
                'bg-red-100 text-red-700'
          )}>
            {userStory.engagement.score}
          </div>
          <div>
            <p className='text-base font-medium text-blue-900'>Engagement Score</p>
            <p className='text-xs font-medium text-green-600'>↑ higher is better</p>
            <p className='mt-0.5 text-sm text-blue-700'>
              {userStory.engagement.commentCount} comment{userStory.engagement.commentCount !== 1 ? 's' : ''} · {userStory.engagement.ticketsCreated} created{userStory.engagement.estimateChanges > 0 ? ` · ${userStory.engagement.estimateChanges} re-estimated` : ''}{(() => { const total = userStory.prLinks.filter(p => !p.excludedFromScore).reduce((s, p) => s + (p.linesAdded ?? 0), 0); return total > 0 ? ` · +${total.toLocaleString()} lines` : '' })()}
            </p>
          </div>
          <div className='pointer-events-none absolute bottom-full left-0 z-10 mb-2 w-72 rounded-lg bg-indigo-600 p-3 text-xs text-white opacity-0 shadow-lg transition-opacity group-hover:opacity-100'>
            <p className='mb-1.5 font-semibold'>How this is calculated</p>
            <p className='mb-1 text-gray-300'>35% — Status moves: what proportion of tickets were moved during the sprint? Moves clustered in the last 25% of the sprint are penalised.</p>
            <p className='mb-1 text-gray-300'>35% — Comment coverage: what proportion of tickets were commented on? Each additional comment per ticket adds a small depth bonus (up to 2 extra).</p>
            <p className='mb-1 text-gray-300'>20% — Code output: lines added via merged PRs (ceiling 3,000 lines = full score). Lines removed count at 30% weight.</p>
            <p className='text-gray-300'>10% — Transparency: tickets created + estimates updated (capped at 5 actions), scaled by delivery ratio. No delivery = no bonus.</p>
          </div>
        </div>
        <div className='group relative rounded-lg bg-purple-50 p-4 flex items-center gap-4'>
          <div className={clsx(
            'flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full text-xl font-bold',
            userStory.dynamic.score <= 30 ? 'bg-green-100 text-green-700' :
              userStory.dynamic.score <= 60 ? 'bg-yellow-100 text-yellow-700' :
                'bg-red-100 text-red-700'
          )}>
            {userStory.dynamic.score}
          </div>
          <div>
            <p className='text-base font-medium text-purple-900'>Scope Volatility</p>
            <p className='text-xs font-medium text-green-600'>↓ lower is better</p>
            <p className='mt-0.5 text-sm text-purple-700'>{
              (() => {
                const added = changes
                  ? (changes.bugsAdded ?? 0) + (changes.addedIssues?.filter((i: any) => i.type !== 'Bug').length ?? 0)
                  : userStory.dynamic.ticketsAdded
                const removed = changes
                  ? (changes.bugsRemoved ?? 0) + (changes.removedIssues?.filter((i: any) => i.type !== 'Bug').length ?? 0)
                  : userStory.dynamic.ticketsRemoved
                const addedPts = userStory.scopeChanges
                  .filter(sc => sc.type === 'added')
                  .reduce((s, sc) => s + (sc.points ?? 0), 0)
                const removedPts = userStory.scopeChanges
                  .filter(sc => sc.type === 'removed')
                  .reduce((s, sc) => s + (sc.points ?? 0), 0)
                const netPts = addedPts - removedPts
                const ptsPart = netPts !== 0
                  ? ` · ${netPts > 0 ? '+' : ''}${netPts} pts`
                  : ''
                return `${added} added · ${removed} removed${ptsPart}`
              })()
            }</p>
          </div>
          <div className='pointer-events-none absolute bottom-full left-0 z-10 mb-2 w-72 rounded-lg bg-indigo-600 p-3 text-xs text-white opacity-0 shadow-lg transition-opacity group-hover:opacity-100'>
            <p className='mb-1.5 font-semibold'>How Scope Volatility is calculated</p>
            <p className='mb-1 text-gray-300'>Measures how much the sprint scope changed from start to end — combining points variance (60% weight) and ticket count variance (40% weight).</p>
            <p className='mb-1 text-gray-300'>Points variance compares total committed points vs total end-of-sprint points (all tickets, not just delivered). Ticket variance compares start vs end ticket count.</p>
            <p className='mb-1 text-gray-300'>0–20% change → score 0–40 (stable)</p>
            <p className='mb-1 text-gray-300'>20–50% change → score 40–75 (volatile)</p>
            <p className='text-gray-300'>50%+ change → score 75–100 (highly volatile)</p>
          </div>
        </div>
      </div>

      <div className='rounded-lg border border-gray-100 bg-gray-50 p-4 space-y-3'>
        <div className='flex gap-4'>
          {chain.length > 0 && (
            <div className='flex-[2]'>
              <p className='mb-2 text-xs font-medium uppercase tracking-wide text-gray-400'>Ticket Flow</p>
              <div className='flex flex-wrap items-center gap-1'>
                {chain.map(({ node, countToNext }) => {
                  const nodeRejections = rejectionsBySource.get(node) ?? []
                  const totalRejections = nodeRejections.reduce((s, r) => s + r.count, 0)

                  return (
                    <React.Fragment key={node}>
                      <div className='group/node relative flex items-center'>
                        <span className='rounded-full bg-white border border-gray-200 px-3 py-1 text-sm text-gray-700'>{node}</span>
                        {totalRejections > 0 && (
                          <>
                            <span className='absolute -top-1.5 -right-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white ring-1 ring-white'>
                              {totalRejections}
                            </span>
                            <div className='pointer-events-none absolute bottom-full left-1/2 z-10 mb-2 -translate-x-1/2 whitespace-nowrap rounded-lg bg-indigo-600 px-3 py-1.5 text-xs text-white opacity-0 shadow-lg transition-opacity group-hover/node:opacity-100'>
                              {nodeRejections.map(r => (
                                <div key={r.to}>{r.count}× rejected back to {r.to}</div>
                              ))}
                            </div>
                          </>
                        )}
                      </div>
                      {countToNext !== undefined && (
                        <div className='flex items-center gap-0.5'>
                          <ArrowRightIcon className='h-3 w-3 text-gray-400' />
                          <span className='rounded-full bg-indigo-100 px-2 py-0.5 text-xs font-semibold text-indigo-700'>{countToNext}</span>
                          <ArrowRightIcon className='h-3 w-3 text-gray-400' />
                        </div>
                      )}
                    </React.Fragment>
                  )
                })}
              </div>
            </div>
          )}
          {stuckKeys.length > 0 && (
            <div className='flex-[1] min-w-0'>
              <p className='mb-2 text-xs font-medium uppercase tracking-wide text-gray-400'>
                No Movement — {stuckKeys.length} ticket{stuckKeys.length !== 1 ? 's' : ''}
              </p>
              <div className='flex flex-wrap gap-2'>
                {(showAllStuck ? stuckKeys : stuckKeys.slice(0, 5)).map(key => (
                  <a
                    key={key}
                    href={`https://cnsgitbits.atlassian.net/browse/${key}`}
                    target='_blank'
                    rel='noopener noreferrer'
                    className='rounded-full bg-red-50 border border-red-200 px-3 py-1 text-sm text-red-700 hover:bg-red-100 transition-colors'
                  >
                    {key}
                  </a>
                ))}
                {stuckKeys.length > 5 && !showAllStuck && (
                  <button
                    onClick={() => setShowAllStuck(true)}
                    className='rounded-full bg-gray-100 border border-gray-200 px-3 py-1 text-sm text-gray-500 hover:bg-gray-200 transition-colors'
                  >
                    +{stuckKeys.length - 5} more
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
        {totalComments > 0 && (
          <div className='border-t border-gray-200 pt-3'>
            <p className='mb-2 text-xs font-medium uppercase tracking-wide text-gray-400'>Comments — {totalComments} total</p>
            <div className='flex flex-wrap gap-2'>
              {([...userStory.commentActivity].sort((a, b) => b.commentCount - a.commentCount))
                .slice(0, showAllComments ? undefined : 6)
                .map(a => (
                  <a
                    key={a.issueKey}
                    href={`https://cnsgitbits.atlassian.net/browse/${a.issueKey}`}
                    target='_blank'
                    rel='noopener noreferrer'
                    className='flex items-center gap-1.5 rounded-full bg-white border border-gray-200 px-3 py-1 text-sm hover:border-purple-300 hover:bg-purple-50 transition-colors'
                  >
                    <ChatBubbleLeftRightIcon className='h-3 w-3 text-purple-400 flex-shrink-0' />
                    <span className='text-gray-600'>{a.issueKey}</span>
                    <span className='font-medium text-gray-800'>{a.commentCount}</span>
                  </a>
                ))}
              {userStory.commentActivity.length > 6 && !showAllComments && (
                <button
                  onClick={() => setShowAllComments(true)}
                  className='rounded-full bg-gray-100 border border-gray-200 px-3 py-1 text-sm text-gray-500 hover:bg-gray-200 transition-colors'
                >
                  +{userStory.commentActivity.length - 6} more
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      <ActivityChart userStory={userStory} />

      <p className='text-sm font-medium text-gray-600'>Timeline</p>
      {timelineEvents.length > 0 ? (
        <div className='relative'>
          <div className='absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200' />
          <div className='space-y-4'>
            {timelineEvents.map((event, index) => (
              <div key={index} className='relative flex items-start pl-10'>
                <div className='absolute left-0 flex h-8 w-8 items-center justify-center rounded-full bg-white ring-2 ring-gray-200'>
                  {getEventIcon(event.type)}
                </div>
                <div className='flex-1 rounded-md border border-gray-100 bg-gray-50 p-3'>
                  <div className='flex items-center justify-between'>
                    <div className='flex items-center'>
                      {getEventLabel(event)}
                    </div>
                    <div className='flex items-center text-xs text-gray-500'>
                      <ClockIcon className='mr-1 h-3 w-3' />
                      {formatTime(event.timestamp)}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className='py-4 text-center text-gray-500'>
          No timeline events to display
        </div>
      )}
    </div>
  )
}